import { type ReactElement } from 'react';
import { DashboardDateRangeControls } from '@/features/dashboard/components/DashboardDateRangeControls';
import { formatRangeWindowNoDataText } from '@/features/dashboard/hooks';
import { ExecutionMetricsChartPanel } from '@/features/executions/components/ExecutionMetricsChartPanel';
import { SuiteExecutionsTable } from '@/features/executions/components/SuiteExecutionsTable';
import { MetricValueCardsRow } from '@/features/metrics/components/MetricValueCardsRow';
import { StatusMetricCardsRow } from '@/features/metrics/components/StatusMetricCardsRow';
import { DetailDeleteHeaderAction } from '@/features/shared/components/DetailDeleteHeaderAction';
import { SuiteDetailsPanels } from '@/features/suites/components/SuiteDetailsPanels';
import { TestsTable } from '@/features/tests/components/TestsTable';
import { s } from '@/theme/tokens';
import { TagsFilterField } from '@/ui/composites/filters/TagsFilterField';
import { buildEqFilterValue, buildInFilter, mergeFilters } from '@/ui/composites/grid/gridFilters';
import { Panel } from '@/ui/composites/Panel';
import { DetailPageTemplate, DetailTitlePrefix } from '@/ui/composites/scaffolds';
import { DeltasToggle } from '@/ui/composites/table';
import { PageHeader } from '@/ui/layout/PageHeader';
import { Stack } from '@/ui/layout/Stack';
import { Box } from '@/ui/primitives';
import { Tab, Tabs } from '@/ui/primitives/Tabs';
import { Text } from '@/ui/primitives/Text';
import type { SuiteDetailPageViewModel } from '@/features/suites/hooks/useSuiteDetailPageViewModel';
import type { SuiteDetailDto } from '@flowqa/shared';

type SuiteDetailContentProps = {
  suite: SuiteDetailDto;
  viewModel: SuiteDetailPageViewModel;
  onDelete?: () => Promise<void> | void;
  deleteLoading?: boolean;
};

export const SuiteDetailContent = ({
  suite,
  viewModel,
  onDelete,
  deleteLoading = false,
}: SuiteDetailContentProps): ReactElement => {
  const hasCustomDates = Boolean(viewModel.selectedStartDay ?? viewModel.selectedEndDay);
  const suiteExecutionsNoDataText = formatRangeWindowNoDataText(
    'No suite executions found',
    viewModel.range,
    hasCustomDates,
  );

  return (
    <DetailPageTemplate
      title={
        <Text role="sectionTitle" component="h1">
          <DetailTitlePrefix label="Suite" />
          {viewModel.title}
        </Text>
      }
      headerActions={
        <DetailDeleteHeaderAction
          onDelete={onDelete}
          deleteLoading={deleteLoading}
          dialogTitle="Delete Suite"
          dialogDescription={
            <Stack gap="sm">
              <Text role="body" color="textSecondary">
                Deleting this suite also removes associated tests, suite executions, and test
                executions.
              </Text>
              <Text role="body" color="textSecondary">
                This action cannot be undone.
              </Text>
            </Stack>
          }
          confirmLabel="Delete Suite"
        />
      }
      metadata={<SuiteDetailsPanels suite={suite} />}
      primaryContent={
        <Stack gap="none">
          <Box sx={{ mt: s('md') }}>
            <Stack gap="md">
              <Box
                sx={{
                  borderTop: 1,
                  borderBottom: 1,
                  borderColor: 'divider',
                  py: s('lg'),
                }}
              >
                <PageHeader
                  title="Suite Executions"
                  actions={
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      gap="md"
                      sx={{ alignItems: { xs: 'stretch', md: 'center' } }}
                    >
                      <TagsFilterField
                        value={viewModel.selectedTags}
                        options={viewModel.tagOptions}
                        onChange={viewModel.handleTagsChange}
                        emptyLabel={viewModel.tagOptionsLoading ? 'Loading Tags...' : ''}
                      />
                      <DashboardDateRangeControls
                        value={viewModel.range}
                        onChange={(nextRange) => {
                          viewModel.setRange(nextRange);
                          viewModel.setSelectedStartDay(null);
                          viewModel.setSelectedEndDay(null);
                        }}
                        displayStartDay={viewModel.pickerStartDay}
                        displayEndDay={viewModel.pickerEndDay}
                        isCustomRange={
                          Boolean(viewModel.selectedStartDay ?? viewModel.selectedEndDay) &&
                          !viewModel.isAllMode
                        }
                        onSelectedStartDayChange={viewModel.handleStartDayChange}
                        onSelectedEndDayChange={viewModel.handleEndDayChange}
                        fullWidthOnMobile
                      />
                    </Stack>
                  }
                />
              </Box>
              <Box sx={{ mt: s('md') }}>
                <Stack gap="lg">
                  <StatusMetricCardsRow cards={viewModel.suiteMetricCards} />
                  <MetricValueCardsRow items={viewModel.metricValueItems} />
                </Stack>
              </Box>
              <Box sx={{ mt: s('md') }}>
                <ExecutionMetricsChartPanel
                  chartTab={viewModel.chartTab}
                  tabsAriaLabel="Suite detail chart tabs"
                  onChartTabChange={viewModel.setChartTab}
                  showChartDateClear={viewModel.showChartDateClear}
                  onChartDateClear={viewModel.handleChartDateClear}
                  chartHeight={viewModel.chartHeight}
                  suppressChartAnimation={viewModel.suppressChartAnimation}
                  stackedChartData={viewModel.stackedChartData}
                  passRateChartData={viewModel.passRateChartData}
                  frequencyChartData={viewModel.frequencyChartData}
                  durationChartData={viewModel.durationChartData}
                  passRateGuideLines={viewModel.passRateGuideLines}
                  frequencyGuideLines={viewModel.frequencyGuideLines}
                  durationGuideLines={viewModel.durationGuideLines}
                  onChartSelect={viewModel.handleChartSelect}
                  showLineChartPoints={viewModel.showLineChartPoints}
                  emptyStateTitle={suiteExecutionsNoDataText}
                />
              </Box>
              <Box sx={{ mt: s('lg') }}>
                <Panel padding="md" gap="none" contentGap="md">
                  <Stack gap="md">
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <Stack
                        direction="row"
                        align="center"
                        justify="space-between"
                        sx={{ minWidth: 0 }}
                      >
                        <Tabs
                          value={viewModel.dataTab}
                          onChange={(_, next) => {
                            viewModel.setDataTab(next as 'executions' | 'tests');
                          }}
                          aria-label="Suite detail data tabs"
                        >
                          <Tab label="Executions" value="executions" />
                          <Tab label="Tests" value="tests" />
                        </Tabs>
                        {viewModel.dataTab === 'tests' && viewModel.showTableDeltaToggle ? (
                          <DeltasToggle
                            checked={viewModel.tableDeltasEnabled}
                            onChange={viewModel.setTableDeltasEnabled}
                          />
                        ) : null}
                      </Stack>
                    </Box>
                    {viewModel.dataTab === 'executions' ? (
                      <SuiteExecutionsTable
                        persistKey={`suite-detail-executions-${viewModel.suiteId}`}
                        fixedFilters={viewModel.suiteExecutionFilters}
                        pageSizeOptions={[10]}
                        initialPageSize={10}
                        disablePageSizeSelect
                        showSuiteColumn={false}
                        noRowsText={suiteExecutionsNoDataText}
                      />
                    ) : (
                      <TestsTable
                        persistKey={`suite-detail-tests-${viewModel.suiteId}`}
                        fixedFilters={mergeFilters(
                          buildEqFilterValue('suiteId', viewModel.suiteId),
                          buildInFilter('tags', viewModel.selectedTags),
                        )}
                        pageSizeOptions={[10]}
                        initialPageSize={10}
                        disablePageSizeSelect
                        showDeltas={viewModel.showTableDeltas}
                        showSuiteLinks={false}
                      />
                    )}
                  </Stack>
                </Panel>
              </Box>
            </Stack>
          </Box>
        </Stack>
      }
    />
  );
};
